import { useRef } from 'react'
import { motion, useScroll, useTransform, useMotionTemplate } from 'motion/react'
import { useCompactViewport, useMediaQuery } from './useMediaQuery'

export interface ParallaxProps {
  children: React.ReactNode
  /** Total travel in px across the element's pass through the viewport. */
  distance?: number
  className?: string
}

/**
 * Scroll-linked parallax: the wrapper drifts from +distance/2 to -distance/2
 * as it crosses the viewport, locked to the scrollbar (no smoothing), like
 * the reference site's hero media strip. Decorative only — never put text
 * content that must stay aligned inside it. Static on phones and under
 * prefers-reduced-motion.
 */
export function Parallax({ children, distance = 60, className }: ParallaxProps) {
  const ref = useRef<HTMLDivElement>(null)
  const compact = useCompactViewport()
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)')

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  })
  const y = useTransform(scrollYProgress, [0, 1], [distance / 2, -distance / 2])
  // Full transform string keeps Motion on the hardware-accelerated path.
  const transform = useMotionTemplate`translate3d(0, ${y}px, 0)`

  if (compact || reduced) {
    return (
      <div ref={ref} className={className}>
        {children}
      </div>
    )
  }

  return (
    <motion.div ref={ref} style={{ transform }} className={className}>
      {children}
    </motion.div>
  )
}

export default Parallax
